import { useCallback, useState } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ApiService } from 'api';
import EditNoteDialog from 'dashboard409a/components/EditNoteDialog';
import { StyledTableCell, StyledTableRow } from 'dashboard409a/components/ValuationsTable/common';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { getModelStatusString } from '../../../services/models.types';
import { ModelsStatusDropdown } from '../ModelsStatusDropdown/ModelsStatusDropdown';
import { ModelsTableRowProps } from '../ModelsTable.types';
import { getFullName, renderNotesCell, renderStatusCell, renderValuationLeadCell } from './helpers';

const ModelsTableRow = ({
  modelRow,
  onRowClick,
  onNoteSave,
  onStatusUpdate,
  statusOptionsLoading,
  isNoteLoading = false,
  noteError = null,
}: ModelsTableRowProps) => {
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);

  const isFinalStatus = modelRow.model_status === 3;
  
  const handleStatusChange = useCallback(async (newStatus: string) => {
    try {
      setIsUpdatingStatus(true);

      const apiStatusValue = modelStatusMap[newStatus as keyof typeof modelStatusMap];
      if (apiStatusValue === undefined) {
        enqueueSnackbar('Invalid status selected', { variant: 'error' });
        return;
      }

      const updatedModelRow = { model_status: apiStatusValue, company_measurement_date: modelRow.company_measurement_date };
      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
        modelRow?.company_id.toString(), 
        modelRow?.attio_valuation_id, 
        updatedModelRow
      );

      if (onStatusUpdate && modelRow.id) {
        onStatusUpdate(modelRow.id, apiStatusValue);
      }

      setIsEditingStatus(false);
    } catch {
      enqueueSnackbar('Failed to update model status', { variant: 'error' });
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [modelRow, onStatusUpdate]);

  const handleNoteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsNoteDialogOpen(true);
  }, []);

  const handleNoteSave = useCallback(async (newNote: string): Promise<boolean> => {
    if (onNoteSave) {
      const currentNoteContent = modelRow.note?.content || modelRow.notes || '';
      await onNoteSave(newNote, currentNoteContent);
    }
    // Always close dialog after save attempt
    return false;
  }, [onNoteSave, modelRow]);

  const handleStatusClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingStatus(true);
  }, []);

  return (
    <StyledTableRow
      hover
      tabIndex={-1}
      key={modelRow.id}
      style={{ background: '#ffff' }}
      onClick={event => onRowClick(modelRow, event)}>
      <StyledTableCell align="left">{modelRow.company_name || 'Unknown Company'}</StyledTableCell>
      <StyledTableCell align="left">
        <ModelsStatusDropdown
          currentStatus={getModelStatusString(modelRow.model_status || 0)}
          onStatusChange={handleStatusChange}
          isEditing={isEditingStatus}
          onEditEnd={() => setIsEditingStatus(false)}
          loading={statusOptionsLoading}
        />

        {renderStatusCell({
          modelRow,
          isEditingStatus,
          isUpdatingStatus,
          isFinalStatus,
          onStatusClick: handleStatusClick,
          onEditEnd: () => setIsEditingStatus(false),
        })}
      </StyledTableCell>
      <StyledTableCell 
        align="left" 
        onClick={handleNoteClick}
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        {renderNotesCell(modelRow)}
      </StyledTableCell>
      <StyledTableCell align="left">
        <Typography variant="body2">
          {modelRow.reviewers?.[0] ? getFullName(modelRow.reviewers?.[0]) : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell align="left">
        <Typography variant="body2">
          {modelRow.preparers?.[0] ? getFullName(modelRow.preparers?.[0]) : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell align="left">
        <Typography variant="body2">
          {modelRow.supporting_due_date || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell align="left">
        {renderValuationLeadCell(modelRow)}
      </StyledTableCell>
      <StyledTableCell align="left">
        <Typography variant="body2">
          {modelRow.is_loading_valuation_data ? (
            <Skeleton variant="text" width="80%" height={20} />
          ) : (
            modelRow.valuation_date || '-'
          )}
        </Typography>
      </StyledTableCell>
      <EditNoteDialog
        open={isNoteDialogOpen}
        onClose={() => setIsNoteDialogOpen(false)}
        onSave={handleNoteSave}
        currentNote={modelRow.note?.content || modelRow.notes || ''}
        companyName={modelRow.company_name || 'Unknown Company'}
        isLoading={isNoteLoading}
        error={noteError}
      />
    </StyledTableRow>
  );
};

export default ModelsTableRow;
